import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import { Box, Link, TextField as MuiTextField, Typography } from '@mui/material';
import { EnhancedEncryption } from '@mui/icons-material';
import styled from '@emotion/styled';
import { spacing } from '@mui/system';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Center from '../../components/common/Center';
import GuestGuard from '../../components/guards/GuestGuard';
import MessageAlert from '../../components/common/MessageAlert';
import SubmitButton from '../../components/common/SubmitButton';

import { useAuthStore, useUserMessageStore } from '../../stores';
import { requestInvite, RequestInviteResponse } from '../../apis/authApi';

const TextField = styled(MuiTextField)(spacing);
const Button = styled(SubmitButton)(spacing);

const LockIcon = styled(EnhancedEncryption)`
  width: 60px;
  height: 60px;
  margin: 0 auto ${(props) => props.theme.spacing(5)};
`;

function InviteRequest() {
  const [inviteResponse, setInviteResponse] = React.useState<RequestInviteResponse | null>(null);
  const signOutLocal = useAuthStore.use.signOutLocal();
  const clearMessages = useUserMessageStore.use.clearMessages();

  useEffect(() => {
    clearMessages();
    signOutLocal();
  }, []);

  return (
    <>
      <GuestGuard>
        <Helmet title="Request a Login" />
        <Center>
          <LockIcon color="primary" />
        </Center>
        <Typography component="h1" variant="h3" align="center" gutterBottom>
          Request Access
        </Typography>
        {!inviteResponse && (
          <Typography component="h2" variant="subtitle1" align="center">
            Enter your email below to request an invite
          </Typography>
        )}
        {inviteResponse && (
          <Typography component="h2" variant="subtitle1" align="center">
            Access Request Initiated
          </Typography>
        )}
        <Box mt={4}>
          {!inviteResponse && (
            <Formik
              initialValues={{
                email: '',
                submit: false,
              }}
              validationSchema={Yup.object().shape({
                email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
              })}
              onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                try {
                  const inviteResponse = await requestInvite({ email: values.email });
                  setInviteResponse(inviteResponse);
                  setStatus({ success: true });
                } catch (error: any) {
                  const message = error.message || 'Something went wrong';
                  setStatus({ success: false });
                  setErrors({ submit: message });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <MessageAlert></MessageAlert>
                  <TextField
                    type="email"
                    name="email"
                    label="Email Address"
                    autoComplete="email"
                    autoFocus={true}
                    value={values.email}
                    inputProps={{
                      maxLength: 255,
                    }}
                    error={Boolean(touched.email && errors.email)}
                    fullWidth
                    helperText={touched.email && errors.email}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={5}
                  />
                  <Button my={1} text="Request access" isSubmitting={isSubmitting}></Button>
                </form>
              )}
            </Formik>
          )}
          {inviteResponse && (
            <Box p={3} border="1px solid" borderColor={(theme) => theme.palette.info.main}>
              <Typography variant="subtitle2">
                If your email is valid, you’ll receive an invite for each account you don’t already have access to.
              </Typography>
              {(inviteResponse.companyName || inviteResponse.companyEmail) && (
                <Box mt={2} mb={1}>
                  <Typography align="center" variant="subtitle2" mb={2}>
                    Contact us if you need help:
                  </Typography>
                  {inviteResponse.companyName && (
                    <Typography align="center" variant="subtitle2">
                      {inviteResponse.companyName}
                    </Typography>
                  )}
                  {inviteResponse.companyEmail && (
                    <Typography align="center" variant="subtitle2">
                      <Link align="center" href={`mailto:${inviteResponse.companyEmail}`}>
                        {inviteResponse.companyEmail}
                      </Link>
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </GuestGuard>
    </>
  );
}

export default InviteRequest;
